import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import '@/App.css';
import HomePage from '@/pages/HomePage';
import DirectReservePage from '@/pages/DirectReservePage';
import ContactPage from '@/pages/ContactPage';
import AttestationsPage from '@/pages/AttestationsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import BackToTop from '@/components/BackToTop';
import ScrollToTop from '@/components/ScrollToTop';
import ChatWidget from '@/components/ChatWidget';

// Language wrapper component to handle language detection from URL
const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    // Detect language from URL path
    const pathLang = location.pathname.split('/')[1];
    
    if (lang && ['en', 'fr', 'es'].includes(lang)) {
      // If lang param exists and is valid, use it
      i18n.changeLanguage(lang);
    } else if (pathLang && ['fr', 'es'].includes(pathLang)) {
      // If URL starts with /fr or /es, use that language
      i18n.changeLanguage(pathLang);
    } else {
      // Default to English for root paths or invalid languages
      i18n.changeLanguage('en');
    }
  }, [lang, i18n, location.pathname]);

  return children;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-slate-50">
        <BrowserRouter>
          <ScrollToTop />
          <Navbar />
          
          <Routes>
            {/* English routes (default, no prefix) */}
            <Route path="/" element={<LanguageWrapper><HomePage /></LanguageWrapper>} />
            <Route path="/direct-reserve" element={<LanguageWrapper><DirectReservePage /></LanguageWrapper>} />
            <Route path="/directreserve" element={<Navigate to="/direct-reserve" replace />} />
            <Route path="/contact" element={<LanguageWrapper><ContactPage /></LanguageWrapper>} />
            <Route path="/attestations" element={<LanguageWrapper><AttestationsPage /></LanguageWrapper>} />
            
            {/* French routes */}
            <Route path="/fr" element={<LanguageWrapper><HomePage /></LanguageWrapper>} />
            <Route path="/fr/direct-reserve" element={<LanguageWrapper><DirectReservePage /></LanguageWrapper>} />
            <Route path="/fr/directreserve" element={<Navigate to="/fr/direct-reserve" replace />} />
            <Route path="/fr/contact" element={<LanguageWrapper><ContactPage /></LanguageWrapper>} />
            <Route path="/fr/attestations" element={<LanguageWrapper><AttestationsPage /></LanguageWrapper>} />
            
            {/* Spanish routes */}
            <Route path="/es" element={<LanguageWrapper><HomePage /></LanguageWrapper>} />
            <Route path="/es/direct-reserve" element={<LanguageWrapper><DirectReservePage /></LanguageWrapper>} />
            <Route path="/es/directreserve" element={<Navigate to="/es/direct-reserve" replace />} />
            <Route path="/es/contact" element={<LanguageWrapper><ContactPage /></LanguageWrapper>} />
            <Route path="/es/attestations" element={<LanguageWrapper><AttestationsPage /></LanguageWrapper>} />
            
            {/* Legacy route redirects for SEO */}
            <Route path="/vaultchain" element={<DirectReservePage />} />
            <Route path="/gold" element={<DirectReservePage />} />
            <Route path="/about-us" element={<HomePage />} />
            
            {/* Legacy routes with language prefix */}
            <Route path="/fr/vaultchain" element={<LanguageWrapper><DirectReservePage /></LanguageWrapper>} />
            <Route path="/es/vaultchain" element={<LanguageWrapper><DirectReservePage /></LanguageWrapper>} />
            
            {/* 404 Catch-all route - must be last */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer />
          <BackToTop />
          <ChatWidget />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;